import React, { useEffect, useMemo, useState } from 'react';

import { formatDateString } from '~libs/dayjs';
import { CareRecipientProblem, OverwriteDetails, readOverwriteDetails } from '~support-at-home/lib/careRecipientErrors';

import { Button } from '~components';
import Select from '~components/form-fields/inputs/Select';
import Modal from '~components/Modal';

import { CareRecipient } from '~support-at-home/models/careRecipients';

import { useGetLinkableClientsQuery } from '~support-at-home/actions/careRecipients';

interface ComponentProps {
  careRecipient: CareRecipient | null;
  isOpen: boolean;
  isSubmitting?: boolean;
  onCancel: () => void;
  // Resolves to null when the link was made (the caller closes the window), or to the problem the
  // API reported so the window can show it.
  onSubmit: (clientId: number, confirmOverwrite: boolean) => Promise<CareRecipientProblem | null>;
}

type Step = 'select' | 'confirm';

const blockedMessage = (problem: CareRecipientProblem): string => {
  if (problem.reasonCode === 'held_by_other') {
    const holder = typeof problem.data.client_name === 'string' ? problem.data.client_name : 'Another client';
    return `${holder} already holds this Care Recipient ID in its Medicare Ref. Change that client's Medicare Ref. first.`;
  }
  return problem.message;
};

const CareRecipientLinkModal: React.FC<ComponentProps> = ({
  careRecipient,
  isOpen,
  isSubmitting = false,
  onCancel,
  onSubmit,
}) => {
  const suggestion = careRecipient?.suggested_client ?? null;
  // Unlink only changes the screen, so an Unlinked row's client still holds the Care Recipient ID and is
  // the only client a link can succeed with: start from it, otherwise from the suggestion.
  const unlinkedClientId =
    careRecipient?.match_status === 'unlinked' ? (careRecipient.linked_client?.client_id ?? null) : null;
  const initialClientId = unlinkedClientId ?? suggestion?.client_id ?? null;
  const [clientId, setClientId] = useState<number | null>(null);
  const [step, setStep] = useState<Step>('select');
  const [overwrite, setOverwrite] = useState<OverwriteDetails | null>(null);
  const [problem, setProblem] = useState<CareRecipientProblem | null>(null);

  const {
    data: clients,
    isFetching: isLoadingClients,
    isError: isClientsError,
  } = useGetLinkableClientsQuery(undefined, { skip: !isOpen });

  // Start from the unlinked client or the suggestion (or empty) each time the window opens for a care
  // recipient, so an earlier choice, confirm step or error never carries over.
  useEffect(() => {
    if (!isOpen) return;
    setClientId(initialClientId);
    setStep('select');
    setOverwrite(null);
    setProblem(null);
  }, [isOpen, careRecipient?.id, initialClientId]);

  const options = useMemo(
    () => (clients ?? []).map((c) => ({ id: c.id, name: c.disabled ? `${c.name} (Disabled)` : c.name })),
    [clients],
  );
  const selectedName = options.find((o) => o.id === clientId)?.name ?? null;
  const isSuggestionSelected = suggestion !== null && clientId === suggestion.client_id;

  const submit = async (confirmOverwrite: boolean) => {
    if (clientId === null) return;
    const result = await onSubmit(clientId, confirmOverwrite);
    if (!result) return;
    if (result.status === 409 && result.reasonCode === 'confirm_overwrite') {
      setOverwrite(readOverwriteDetails(result));
      setProblem(null);
      setStep('confirm');
      return;
    }
    setProblem(result);
    setStep('select');
  };

  const handleClientChange = (_id: string, value: number | null) => {
    setClientId(value);
    setProblem(null);
  };

  const setIsOpen = (open: boolean) => {
    if (!open && !isSubmitting) onCancel();
  };

  const careRecipientId = careRecipient?.care_recipient_id ?? '';

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      header="Link care recipient"
      includeFooter={false}
      backdrop="static"
      disableClose={isSubmitting}
    >
      <div className="d-flex flex-column gap-2" data-cy="sah-care-recipient-link-body">
        <div>
          <strong>Care Recipient ID:</strong> {careRecipientId || '-'}
        </div>
        <div>
          <strong>Name:</strong> {careRecipient?.name || '-'}
        </div>
        <div>
          <strong>DOB:</strong> {formatDateString(careRecipient?.birth_date) || '-'}
        </div>

        {step === 'select' && (
          <>
            {isSuggestionSelected && (
              <div className="alert alert-info mb-0 py-2 mt-2" role="note" data-cy="sah-care-recipient-link-suggestion">
                <strong>Suggested match, not yet linked.</strong>{' '}
                <strong>{suggestion?.client_name ?? 'This client'}</strong> has the same name and date of birth. Confirm
                it with Link, or choose a different client below.
              </div>
            )}
            <label htmlFor="sah-care-recipient-link-client" className="mt-2 fw-semibold">
              TurnPoint client
            </label>
            <Select
              type="select"
              id="sah-care-recipient-link-client"
              name="client_id"
              value={clientId ?? undefined}
              options={options}
              placeholder={isLoadingClients ? 'Loading clients…' : 'Search clients…'}
              noOptionsMessage="No clients found"
              disabled={isLoadingClients || isSubmitting}
              dataCy="sah-care-recipient-link-client"
              onChange={handleClientChange}
            />
            {isClientsError && (
              <div
                className="alert alert-danger mb-0 py-2"
                role="alert"
                data-cy="sah-care-recipient-link-clients-error"
              >
                The client list could not be loaded. Close this window and try again.
              </div>
            )}
            {problem && (
              <div className="alert alert-danger mb-0 py-2" role="alert" data-cy="sah-care-recipient-link-alert">
                {blockedMessage(problem)}
              </div>
            )}
            <div className="d-flex justify-content-end gap-2 mt-3">
              <Button
                id="sah-care-recipient-link-cancel"
                data-cy="sah-care-recipient-link-cancel"
                color="secondary"
                onClick={onCancel}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                id="sah-care-recipient-link-confirm"
                data-cy="sah-care-recipient-link-confirm"
                color="success"
                onClick={() => submit(false)}
                disabled={clientId === null || isSubmitting || isLoadingClients}
              >
                Link
              </Button>
            </div>
          </>
        )}

        {step === 'confirm' && (
          <>
            <div
              className="alert alert-warning mb-0 py-2 mt-2"
              role="alert"
              data-cy="sah-care-recipient-link-overwrite"
            >
              Medicare Ref. for <strong>{overwrite?.clientName ?? selectedName ?? 'this client'}</strong> will change
              from <strong data-cy="sah-care-recipient-link-old-ref">{overwrite?.oldMedicareRef ?? '-'}</strong> to{' '}
              <strong data-cy="sah-care-recipient-link-new-ref">{overwrite?.newMedicareRef ?? careRecipientId}</strong>.
              The change is recorded in the client&apos;s audit log.
            </div>
            <div className="d-flex justify-content-end gap-2 mt-3">
              <Button
                id="sah-care-recipient-link-back"
                data-cy="sah-care-recipient-link-back"
                color="secondary"
                onClick={() => setStep('select')}
                disabled={isSubmitting}
              >
                Back
              </Button>
              <Button
                id="sah-care-recipient-link-overwrite-confirm"
                data-cy="sah-care-recipient-link-overwrite-confirm"
                color="success"
                onClick={() => submit(true)}
                disabled={isSubmitting}
              >
                Change and link
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default CareRecipientLinkModal;
